import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateRangeControl, defaultRange } from '@/components/shared';
import { getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import { useBusinessAnalytics, type BusinessAnalyticsRow } from './api/useBusinessAnalytics';
import AnalyticsTable, { COLUMNS } from './components/AnalyticsTable';

// Convert a YYYY-MM-DD date string to start- or end-of-day in UTC ISO format.
function dateToIsoStart(date: string): string {
  return `${date}T00:00:00.000Z`;
}
function dateToIsoEnd(date: string): string {
  return `${date}T23:59:59.999Z`;
}

function rowsToCsv(rows: BusinessAnalyticsRow[]): string {
  const header = COLUMNS.map((c) => c.label).join(',');

  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const body = rows.map((row) =>
    COLUMNS.map((col) => escape(row[col.key])).join(','),
  );

  return [header, ...body].join('\n');
}

function downloadCsv(rows: BusinessAnalyticsRow[], fromDate: string, toDate: string) {
  const csv = rowsToCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  let suffix: string;
  if (!fromDate && !toDate) {
    suffix = `${today}-alltime`;
  } else if (fromDate && toDate) {
    suffix = `${fromDate}_to_${toDate}`;
  } else if (fromDate) {
    suffix = `from_${fromDate}`;
  } else {
    suffix = `until_${toDate}`;
  }
  a.href = url;
  a.download = `business-analytics-${suffix}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function BusinessAnalyticsPage(): React.ReactElement {
  const apiUrl = getAdminApiUrl('prod');
  const [[fromDate, toDate], setRange] = useState<[string, string]>(defaultRange);

  const queryParams = useMemo(() => {
    const params: { since?: string; until?: string } = {};
    if (fromDate) params.since = dateToIsoStart(fromDate);
    if (toDate) params.until = dateToIsoEnd(toDate);
    return params;
  }, [fromDate, toDate]);

  const { data, loading, error, refetch } = useBusinessAnalytics(apiUrl, queryParams);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.businesses;
    return data.businesses.filter((b) =>
      b.business_name.toLowerCase().includes(q),
    );
  }, [data.businesses, search]);

  const dateLabel = useMemo(() => {
    if (!fromDate && !toDate) return 'All time';
    if (fromDate && toDate && fromDate === toDate) return fromDate;
    if (fromDate && toDate) return `${fromDate} → ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    return `Until ${toDate}`;
  }, [fromDate, toDate]);

  const dateInvalid = Boolean(fromDate && toDate && fromDate > toDate);

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-[1800px] mx-auto px-6 pt-8 pb-16">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/b2b"
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors no-underline"
          >
            B2B Dashboards
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">Business Insights</span>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white font-display">
              Business Insights
            </h1>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
              PRODUCTION
            </span>
            <span className="text-xs text-gray-500">{dateLabel}</span>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by business name"
              className="w-64 px-3 py-2 text-sm bg-gray-800 border border-gray-700 text-gray-200 placeholder-gray-500 rounded-lg focus:outline-none focus:border-violet-500"
            />
            <Link
              to="/b2b/business-analytics/full-report"
              className="group relative inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-violet-100 bg-gradient-to-r from-violet-600/30 via-fuchsia-600/30 to-violet-600/30 border border-violet-500/60 rounded-lg hover:border-violet-400/80 hover:shadow-lg hover:shadow-violet-900/40 transition-all no-underline"
            >
              <svg className="w-4 h-4 text-violet-200 transition-transform group-hover:rotate-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
              </svg>
              Full Report
            </Link>
            <button
              onClick={() => downloadCsv(filtered, fromDate, toDate)}
              disabled={loading || filtered.length === 0}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Export CSV
            </button>
            <button
              onClick={refetch}
              disabled={loading}
              className="px-3 py-2 text-sm font-medium text-violet-300 bg-violet-900/30 border border-violet-700/50 rounded-lg hover:bg-violet-900/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Date filter row */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center gap-3 px-4 py-3 bg-[#181831] border border-gray-800/80 rounded-xl">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Date Range</span>
          </div>

          <DateRangeControl
            from={fromDate || null}
            to={toDate || null}
            onChange={(f, t) => setRange([f ?? '', t ?? ''])}
          />
        </div>

        {dateInvalid && (
          <div className="mb-4 px-4 py-2 bg-amber-950/40 border border-amber-800/50 rounded-lg text-sm text-amber-300">
            From date is after To date — adjust to load data.
          </div>
        )}

        {/* Summary strip */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm text-gray-400">
            Showing <span className="text-white font-semibold">{filtered.length}</span> of{' '}
            <span className="text-white font-semibold">{data.business_count}</span> active production
            businesses
            {loading && <span className="ml-2 text-violet-400">· loading…</span>}
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs text-violet-300/80">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            Tip — click a business name to open its full analytics page
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <AnalyticsTable rows={filtered} loading={loading} />
      </div>
    </div>
  );
}

export default BusinessAnalyticsPage;
